import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home as HomeIcon,
  Radio,
  Grid3x3,
  Search as SearchIcon,
  MoreHorizontal,
  Play,
  Plus,
  Check,
  Info,
  ChevronLeft,
  ChevronRight,
  Download,
  Smartphone,
  Tv as TvIcon,
  Monitor,
  ExternalLink,
  User,
  Lock,
  Clock,
  Bell,
  Flame,
  Pencil,
  EyeOff,
  Ticket,
  PlayCircle,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSpatialNav, useWakeLock } from "../../lib/useSpatialNav";
import TvPlayer from "./TvPlayer";
import TvLink from "./TvLink";
import WatchHub from "../WatchHub";

/**
 * TvApp — Tap IN TV Smart-TV web app (Netflix-style).
 *
 * Targets Samsung / LG / Fire TV / Android TV browsers. All interactive
 * elements carry `data-tv-focus="true"` so the shared spatial-nav hook can
 * move focus with arrow keys. Enter/Space activates; Back/Escape navigates.
 *
 * Layout mirrors Netflix: translucent top nav · rotating hero banner with
 * muted trailer preview · dense 16:9 carousels (Continue Watching, My List,
 * Trending, category rails) · focused cards scale and reveal title detail.
 */
const TABS = [
  { key: "home",     label: "Home",     icon: HomeIcon },
  { key: "watch",    label: "Watch",    icon: PlayCircle },
  { key: "live",     label: "Live",     icon: Radio },
  { key: "channels", label: "Channels", icon: Grid3x3 },
  { key: "ppv",      label: "PPV",      icon: Ticket },
  { key: "search",   label: "Search",   icon: SearchIcon },
  { key: "more",     label: "More",     icon: MoreHorizontal },
];

export default function TvApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = (new URLSearchParams(location.search).get("tab")) || "home";
  const [tab, setTab] = useState(TABS.find((t) => t.key === initialTab) ? initialTab : "home");
  const [playing, setPlaying] = useState(null); // { id, title, src, kind }

  // Prevent screensaver during playback
  useWakeLock(!!playing);

  // Arrow-key navigation. Back closes the player, or leaves TV mode.
  useSpatialNav(() => {
    if (playing) setPlaying(null);
    else navigate("/");
  });

  // Whenever the active tab changes, ensure focus lands on a real
  // focusable inside the new tab so subsequent Tab / PageDown / arrow
  // presses have a starting point. Without this, Smart-TV browsers park
  // focus on <body> and the spatial-nav hook falls back to scrolling
  // document — which is locked because the TV root is `overflow: hidden`.
  useEffect(() => {
    const t = setTimeout(() => {
      const body = document.querySelector('[data-testid="tv-body"]');
      if (!body) return;
      // Scroll the new tab to the top so PageDown starts at the top.
      try { body.scrollTo({ top: 0, behavior: "auto" }); } catch { /* ignore */ }
      const first = body.querySelector('[data-tv-focus="true"]:not([disabled])');
      if (first && document.activeElement !== first) {
        try { first.focus({ preventScroll: true }); } catch { /* ignore */ }
      }
    }, 120);
    return () => clearTimeout(t);
  }, [tab]);

  return (
    <div
      data-testid="tv-app"
      className="tv-root fixed inset-0 bg-[#0a0a0a] text-white overflow-hidden flex flex-col"
      style={{
        fontSize: "clamp(17px, 1.45vmin, 34px)",
        "--tv-safe-x": "max(28px, 3.6vmin)",
        "--tv-safe-y": "max(18px, 2.6vmin)",
        // Netflix-style smaller, denser cards (was 22vmin → now 15vmin)
        "--tv-card-w": "clamp(140px, 12vmin, 280px)",
        "--tv-hero-h": "clamp(460px, 72vmin, 1320px)",
        "--tv-gap":    "clamp(10px, 1.3vmin, 28px)",
        "--tv-rail-gap": "clamp(6px, 0.7vmin, 14px)",
      }}
    >
      {/* Top nav */}
      <header
        className="flex items-center bg-gradient-to-b from-black/90 via-black/50 to-transparent z-30 absolute inset-x-0 top-0"
        style={{ padding: "var(--tv-safe-y) var(--tv-safe-x)", gap: "var(--tv-gap)" }}
      >
        <button
          data-testid="tv-header-logo"
          onClick={() => setTab("home")}
          className="shrink-0 p-1 -ml-1 bg-transparent border-0 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 [&:focus]:!shadow-none [&:focus-visible]:!shadow-none"
          style={{ WebkitTapHighlightColor: "transparent" }}
          aria-label="Go to Home"
          title="Home"
        >
          <img
            src="/tapintv-logo-v2.png"
            alt="Tap IN TV"
            className="w-auto block"
            style={{ height: "clamp(52px, 5vw, 150px)" }}
            draggable={false}
          />
        </button>
        <nav className="flex items-center gap-1 md:gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                data-tv-focus="true"
                data-testid={`tv-nav-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`tv-focusable flex items-center gap-2 px-[1.2em] py-[0.55em] rounded-full text-[1.05em] font-display font-bold transition ${
                  active
                    ? "bg-white text-black"
                    : "bg-transparent text-white/85 hover:bg-white/10"
                }`}
              >
                <Icon className="w-[1.1em] h-[1.1em]" /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="tv-guide-btn"
            onClick={() => navigate("/tv-guide")}
            className="tv-focusable inline-flex items-center gap-1.5 px-[1em] py-[0.45em] rounded-full text-[0.9em] font-display font-bold bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white shadow-lg"
            aria-label="Open TV Guide"
            title="TV Guide"
          >
            📺 TV Guide
          </button>
          <button
            data-testid="tv-kids-btn"
            onClick={() => navigate("/tv/kids")}
            className="tv-focusable inline-flex items-center gap-1.5 px-[1em] py-[0.45em] rounded-full text-[0.9em] font-display font-bold bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-400 hover:to-pink-400 text-white shadow-lg"
            aria-label="Open Kids TV"
            title="Kids TV"
          >
            🧸 Kids
          </button>
          <div className="text-[0.8em] text-white/45 hidden lg:block">
            ← → ↑ ↓ navigate · ⏎ select · ⇦ back
          </div>
        </div>
      </header>

      {/* Body — full-bleed, header overlays */}
      <main
        data-testid="tv-body"
        key={tab}
        className="tv-body flex-1 overflow-y-auto overflow-x-hidden"
        style={{
          scrollPaddingTop: "calc(var(--tv-safe-y) * 2 + 4em)",
          scrollBehavior: "smooth",
        }}
      >
        {tab === "home" && <HomeTab onPlay={(v) => {
          // Radio cards deep-link to the radio player instead of trying
          // to play in TvPlayer (TV player can't decode HLS audio-only).
          if (v && v._radio && v.embed_url) { navigate(v.embed_url); return; }
          setPlaying(v);
        }} />}
        {tab === "watch" && (
          <div className="pt-[calc(var(--tv-safe-y)*2+4em)] pb-[var(--tv-safe-y)]">
            <WatchHub embedded />
          </div>
        )}
        {tab === "live" && <LiveTab onPlay={setPlaying} />}
        {tab === "channels" && <ChannelsTab onPlay={setPlaying} />}
        {tab === "ppv" && <PpvTab onPlay={setPlaying} />}
        {tab === "search" && <SearchTab onPlay={setPlaying} />}
        {tab === "more" && <MoreTab />}
      </main>

      {/* Full-screen player overlay */}
      {playing && (
        <div className="fixed inset-0 z-50 bg-black" data-testid="tv-player-overlay">
          <TvPlayer video={playing} onClose={() => setPlaying(null)} />
        </div>
      )}

      {/* 10-foot focus-ring + Netflix-aesthetic rail scrollbars hidden */}
      <style>{`
        /* Use plain :focus too, not just :focus-visible, because some Smart-TV
           browsers (older Tizen / webOS / Fire TV) don't set the
           focus-visible heuristic for remote button presses, so the user
           would lose the focus-ring and not know what's selected. */
        .tv-focusable:focus,
        .tv-focusable:focus-visible,
        [data-tv-focus="true"]:focus,
        [data-tv-focus="true"]:focus-visible {
          outline: 3px solid #fff;
          outline-offset: 2px;
          box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.18), 0 12px 34px -6px rgba(0,0,0,0.9);
          transition: outline-offset 120ms ease, box-shadow 180ms ease, transform 180ms ease;
        }
        [data-tv-focus="true"]:focus,
        [data-tv-focus="true"]:focus-visible {
          transform: scale(1.08);
          z-index: 10;
        }

        /* Body scroll — invisible scrollbar, still scrollable (Netflix) */
        .tv-body {
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }
        .tv-body::-webkit-scrollbar { display: none; width: 0; height: 0; }

        /* Rails — completely hidden scrollbar */
        .tv-rail {
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          scroll-snap-type: x proximity;
          scroll-padding-left: var(--tv-safe-x);
        }
        .tv-rail::-webkit-scrollbar { display: none; width: 0; height: 0; }
        .tv-rail > * { scroll-snap-align: start; }

        .tv-rail-wrap {
          position: relative;
          padding-left: var(--tv-safe-x);
          padding-right: var(--tv-safe-x);
        }
        .tv-rail-wrap .tv-rail-arrow {
          opacity: 0;
          transition: opacity 200ms ease;
        }
        .tv-rail-wrap:hover .tv-rail-arrow,
        .tv-rail-wrap:focus-within .tv-rail-arrow {
          opacity: 1;
        }

        /* Hero dots */
        .tv-hero-dot { transition: width 300ms ease, background 300ms ease; }

        /* Card focus: title block slides up */
        .tv-card-title { opacity: 0; transform: translateY(8px); transition: opacity 200ms ease, transform 200ms ease; }
        .tv-card:focus-visible .tv-card-title,
        .tv-card:hover .tv-card-title { opacity: 1; transform: translateY(0); }

        /* Netflix-style hover scale — focused card grows + rises, others stay put.
           The parent <div class="tv-rail"> pads-bottom to prevent clipping. */
        .tv-card-scale {
          transform-origin: left center;
          transition: transform 260ms cubic-bezier(.2,.8,.2,1), box-shadow 220ms ease;
          will-change: transform;
        }
        .tv-card-scale:hover,
        .tv-card-scale:focus-visible {
          transform: scale(1.18) translateY(-6px);
          z-index: 20;
          box-shadow: 0 20px 44px -8px rgba(0,0,0,0.85);
        }
        /* Last-in-row cards grow leftward so they don't clip the viewport. */
        .tv-rail > .tv-card-scale:last-child { transform-origin: right center; }
        .tv-rail { padding-top: 14px; padding-bottom: 28px; }
      `}</style>
    </div>
  );
}

// ==========================================================================
// HOME — Netflix style with rotating hero + Continue Watching + My List
// ==========================================================================
function HomeTab({ onPlay }) {
  const { user } = useAuth();
  const [feed, setFeed] = useState(null);
  const [history, setHistory] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [adminHero, setAdminHero] = useState([]);
  const [adminRails, setAdminRails] = useState([]);
  const [liveNow, setLiveNow] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [radioStations, setRadioStations] = useState([]);
  const [tvAccess, setTvAccess] = useState({ tier: "free", is_paid: false, role: "viewer" });

  // Admin edit mode — enabled via ?admin=1 when the signed-in user IS admin.
  // Shows pencil/eye buttons over each banner/rail for in-place editing.
  const isAdminEditMode = user?.role === "admin" && (new URLSearchParams(window.location.search).get("admin") === "1");

  useEffect(() => {
    api.get("/videos/home").then((r) => setFeed(r.data)).catch(() => setFeed({}));
    api.get("/tv/hero").then((r) => setAdminHero(r.data || [])).catch(() => setAdminHero([]));
    api.get("/tv/featured-rails").then((r) => setAdminRails(r.data || [])).catch(() => setAdminRails([]));
    api.get("/tv/live-now").then((r) => setLiveNow(r.data || [])).catch(() => setLiveNow([]));
    api.get("/tv/coming-soon").then((r) => setComingSoon(r.data || [])).catch(() => setComingSoon([]));
    // Radio stations rail — Tap In Radio surfaced inside the TV App so
    // users can flip to a station the same way they pick a show. Cards
    // deep-link to /radio/station/:slug (hands off to the radio player).
    api.get("/radio/stations").then((r) => {
      const list = (Array.isArray(r.data) ? r.data : []).filter((s) => s.is_active !== false);
      setRadioStations(list);
    }).catch(() => setRadioStations([]));
    // tv/access requires auth; fall back to free tier for anon.
    if (user) api.get("/tv/access").then((r) => setTvAccess(r.data || {})).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) { setHistory([]); setWatchlist([]); return; }
    api.get("/history").then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
    api.get("/watchlist").then((r) => setWatchlist(r.data || [])).catch(() => setWatchlist([]));
  }, [user?.id || user?.user_id]);

  if (!feed) return <TvLoading />;

  // Filter out finished videos (>95%) from Continue Watching
  const continueWatching = (history || []).filter((v) => {
    const p = v.progress_sec || 0;
    const d = v.duration_sec || v.duration || 0;
    return p > 15 && (d === 0 || p / d < 0.95);
  });

  const isPaid = !!tvAccess.is_paid;

  // Split admin rails by kind so we can render brand hubs / top10 / live_now
  // differently from the standard rails.
  const brandHubs = (adminRails || []).filter((r) => (r.kind || "regular") === "brand_hub");

  const rails = [
    continueWatching.length > 0 && { key: "continue", title: "Continue Watching", items: continueWatching, showProgress: true },
    watchlist.length > 0 && { key: "mylist", title: "My List", items: watchlist },
    // Admin-curated featured rails fan out first so their order is predictable.
    // Brand hubs render as their own block below — exclude them here. Paid-only
    // rails are hidden for free users (not blurred — Netflix does the same).
    ...(adminRails || [])
      .filter((r) => (r.kind || "regular") !== "brand_hub")
      .filter((r) => isPaid || !r.is_paid_only)
      .map((r) => ({
        key: `ar-${r.id}`,
        id: r.id,
        title: r.title,
        subtitle: r.subtitle,
        items: r.kind === "live_now" ? liveNow : (r.videos || []),
        kind: r.kind || "regular",
        isPaid: !!r.is_paid_only,
      })),
    { key: "trending", title: "Trending Now", items: feed.trending || [] },
    { key: "recent", title: "Recently Added", items: feed.recent || [] },
    radioStations.length > 0 && {
      key: "radio",
      title: "Tap In Radio — Live Stations",
      items: radioStations.map((s) => ({
        id: `radio-${s.id}`,
        title: s.name,
        description: s.description || s.category,
        thumbnail_url: s.cover_url || "",
        is_external: true,
        embed_url: `/radio/station/${s.slug || s.id}`,
        kind: "radio",
        _radio: true,
      })),
    },
    ...(feed.rails || []).slice(0, 8).map((r) => {
      const cat = r.category || {};
      const key = cat.slug || cat.id || cat.name || "cat";
      const title = r.title || cat.name || cat.slug || "More";
      return { key, title, items: r.videos || [] };
    }),
  ].filter(Boolean).filter((r) => r.items && r.items.length > 0);

  const heroItems = (() => {
    // Admin-curated hero banners take priority. Shape them into the same
    // object the HeroCarousel expects (title + thumbnail_url + optional id).
    const curated = (adminHero || [])
      .filter((h) => h.image_url)
      .filter((h) => isPaid || !h.is_paid_only)
      .map((h) => ({
        id: h.id,
        title: h.title,
        description: h.description || h.subtitle,
        thumbnail_url: h.image_url,
        trailer_url: h.trailer_url,
        watch_url: h.watch_url,
        badge: h.badge,
        _curated: true,
        _is_paid_only: !!h.is_paid_only,
      }));
    if (curated.length > 0) return curated.slice(0, 5);
    // Fallback to auto-picked trending/recent if admin hasn't curated.
    const pool = [...(feed.trending || []), ...(feed.recent || [])];
    const seen = new Set();
    const list = [];
    for (const v of pool) {
      if (!v?.id || seen.has(v.id)) continue;
      if (!v.thumbnail_url) continue;
      seen.add(v.id);
      list.push(v);
      if (list.length >= 5) break;
    }
    return list;
  })();

  return (
    <div className="pb-[var(--tv-safe-y)] flex flex-col">
      {/* Hero sits flush at the very top — nav floats over it (Netflix pattern) */}
      {heroItems.length > 0 && <HeroCarousel items={heroItems} onPlay={onPlay} />}

      {/* All content rails below hero, with consistent vertical spacing */}
      <div className="flex flex-col" style={{ rowGap: "calc(var(--tv-gap)*2.2)", marginTop: "calc(var(--tv-gap)*0.8)" }}>
        {isAdminEditMode && <AdminEditBanner tier={tvAccess} />}
        {!isPaid && user && <FreeTierNudge />}
        {liveNow.length > 0 && <LiveNowStrip items={liveNow} onPlay={onPlay} />}
        {brandHubs.length > 0 && <BrandHubRow hubs={brandHubs} isAdminEditMode={isAdminEditMode} />}
        {comingSoon.length > 0 && <ComingSoonRow items={comingSoon} isPaid={isPaid} />}
        {rails.map((rail) => (
          <TvRailSwitch
            key={rail.key}
            rail={rail}
            onPlay={onPlay}
            isAdminEditMode={isAdminEditMode}
          />
        ))}
      </div>
    </div>
  );
}

// ── New TV home blocks ─────────────────────────────────────────────────

function AdminEditBanner({ tier }) {
  return (
    <div data-testid="tv-admin-edit-banner" className="mx-[var(--tv-safe-x)] flex items-center gap-3 bg-gradient-to-r from-tv-live/20 to-tv-accent/20 border border-tv-live/40 rounded-xl px-4 py-2.5 text-white">
      <Pencil className="w-4 h-4 text-tv-accent" />
      <div className="flex-1 text-sm">
        <strong>Admin edit mode</strong> — banners, rails and coming-soon drops show inline edit buttons. Click one to jump to Admin → TV App with that item focused.
      </div>
      <a href="/admin" target="_blank" rel="noreferrer" className="text-xs font-black bg-tv-accent text-black px-3 py-1.5 rounded-full inline-flex items-center gap-1">
        Admin <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function FreeTierNudge() {
  return (
    <div data-testid="tv-free-nudge" className="mx-[var(--tv-safe-x)] flex items-center gap-3 bg-gradient-to-r from-amber-500/15 to-fuchsia-500/15 border border-amber-500/30 rounded-xl px-4 py-2.5 text-white">
      <Lock className="w-4 h-4 text-amber-300" />
      <div className="flex-1 text-sm"><strong>You're on the free tier.</strong> Unlock Tap IN originals, ad-free radio and paid-only drops.</div>
      <a href="/pricing" className="text-xs font-black bg-tv-accent text-black px-3 py-1.5 rounded-full">Upgrade</a>
    </div>
  );
}

function LiveNowStrip({ items, onPlay }) {
  return (
    <div data-testid="tv-live-now-strip" className="mx-[var(--tv-safe-x)]">
      <h3 className="font-display font-black text-[1.4em] text-white mb-[calc(var(--tv-rail-gap)*1.6)] inline-flex items-center gap-2">
        <span className="inline-flex items-center gap-1 bg-red-600 text-white font-black text-[10px] uppercase tracking-widest px-2 py-0.5 rounded">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
          </span>
          LIVE
        </span>
        Live on Tap IN right now
      </h3>
      <div className="flex gap-[var(--tv-rail-gap)] overflow-x-auto pb-2 tv-rail">
        {items.map((v) => (
          <button
            key={v.id}
            onClick={() => onPlay?.(toPlayableLive(v))}
            data-testid={`tv-live-item-${v.id}`}
            className="shrink-0 group text-left tv-focusable tv-card"
            data-tv-focus="true"
            style={{ width: "var(--tv-card-w)" }}
          >
            <div className="aspect-video bg-black rounded-md overflow-hidden relative ring-1 ring-red-600/70 group-hover:ring-2 group-hover:ring-red-500 transition-all">
              {v.thumbnail_url
                ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-gradient-to-br from-red-700 to-fuchsia-900" />}
              <div className="absolute top-1.5 left-1.5 bg-red-600 text-white text-[10px] font-black uppercase px-1.5 py-0.5 rounded tracking-wider">LIVE</div>
              {v.viewer_count > 0 && (
                <div className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <Flame className="w-2.5 h-2.5 text-tv-accent" /> {v.viewer_count}
                </div>
              )}
            </div>
            <div className="tv-card-title mt-[0.5em]">
              <div className="font-display font-bold text-[0.95em] text-white truncate">{v.title}</div>
              <div className="text-white/55 text-[0.8em] truncate">{v.host_name || v.category}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function BrandHubRow({ hubs, isAdminEditMode }) {
  const navigate = useNavigate();
  return (
    <div data-testid="tv-brand-hubs" className="mx-[var(--tv-safe-x)]">
      <h3 className="font-display font-black text-xl text-white mb-2">Brand hubs</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {hubs.map((h) => (
          <button
            key={h.id}
            data-testid={`tv-brand-hub-${h.id}`}
            onClick={() => navigate(`/browse?rail=${h.id}`)}
            className="relative aspect-[16/9] rounded-xl overflow-hidden group text-left"
            style={h.brand_color ? { backgroundColor: h.brand_color } : undefined}
          >
            {h.cover_image_url && (
              <img src={h.cover_image_url} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            )}
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/20 to-transparent" />
            <div className="absolute bottom-3 left-3 right-3">
              <div className="font-display font-black text-white text-lg drop-shadow-lg">{h.title}</div>
              {h.subtitle && <div className="text-white/80 text-xs drop-shadow">{h.subtitle}</div>}
            </div>
            {isAdminEditMode && <AdminEditPencil href={`/admin?tab=tv_app&focus=rail:${h.id}`} />}
          </button>
        ))}
      </div>
    </div>
  );
}

function ComingSoonRow({ items, isPaid }) {
  return (
    <div data-testid="tv-coming-soon" className="mx-[var(--tv-safe-x)]">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-4 h-4 text-tv-accent" />
        <h3 className="font-display font-black text-xl text-white">Coming soon</h3>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {items.map((cs) => <ComingSoonTile key={cs.id} item={cs} isPaid={isPaid} />)}
      </div>
    </div>
  );
}

function ComingSoonTile({ item, isPaid }) {
  const [reminded, setReminded] = useState(false);
  const [posterOk, setPosterOk] = useState(true);
  const locked = item.is_paid_only && !isPaid;
  const ms = new Date(item.release_at).getTime() - Date.now();
  const days = Math.max(0, Math.floor(ms / 86_400_000));
  const hours = Math.max(0, Math.floor((ms % 86_400_000) / 3_600_000));
  const label = ms <= 0 ? "Available now" : days > 0 ? `Drops in ${days}d ${hours}h` : `Drops in ${hours}h`;
  const remind = async () => {
    try {
      await api.post(`/tv/coming-soon/${item.id}/remind`);
      setReminded(true);
    } catch {}
  };
  return (
    <div data-testid={`tv-cs-${item.id}`} className="shrink-0 w-48 relative">
      <div className="aspect-[2/3] rounded-lg overflow-hidden relative bg-gradient-to-br from-fuchsia-900 via-rose-900 to-black">
        {item.poster_url && posterOk ? (
          <img src={item.poster_url} alt="" className="w-full h-full object-cover" onError={() => setPosterOk(false)} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center p-3 text-center">
            <div className="font-display font-black text-white/85 text-lg leading-tight drop-shadow">{item.title}</div>
          </div>
        )}
        {locked && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Lock className="w-6 h-6 text-amber-300" />
          </div>
        )}
        <div className="absolute bottom-2 left-2 right-2 text-white">
          <div className="bg-black/70 backdrop-blur-md rounded px-2 py-1 text-[11px] font-bold">{label}</div>
        </div>
      </div>
      <div className="mt-1.5 font-bold text-white text-sm truncate">{item.title}</div>
      {item.subtitle && <div className="text-white/50 text-xs truncate">{item.subtitle}</div>}
      <button
        onClick={remind}
        disabled={reminded || locked}
        data-testid={`tv-cs-remind-${item.id}`}
        className={`mt-1 text-xs font-bold w-full py-1 rounded inline-flex items-center justify-center gap-1 ${
          reminded ? "bg-emerald-500/20 text-emerald-300" : locked ? "bg-white/5 text-white/40 cursor-not-allowed" : "bg-white/10 hover:bg-tv-accent hover:text-black text-white"
        }`}
      >
        <Bell className="w-3 h-3" /> {reminded ? "Reminder set" : "Remind me"}
      </button>
    </div>
  );
}

function AdminEditPencil({ href }) {
  return (
    <a
      href={href}
      target="_blank" rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="absolute top-2 right-2 bg-black/80 hover:bg-tv-accent hover:text-black text-white rounded-full p-1.5 z-10"
    >
      <Pencil className="w-3.5 h-3.5" />
    </a>
  );
}

// Switch between different rail renderers based on kind.
function TvRailSwitch({ rail, onPlay, isAdminEditMode }) {
  if (rail.kind === "top10") {
    return <Top10Rail title={rail.title} items={rail.items} onPlay={onPlay} editHref={rail.id && isAdminEditMode ? `/admin?tab=tv_app&focus=rail:${rail.id}` : null} />;
  }
  return (
    <TvRail
      title={rail.title}
      items={rail.items}
      onPlay={onPlay}
      showProgress={rail.showProgress}
      editHref={rail.id && isAdminEditMode ? `/admin?tab=tv_app&focus=rail:${rail.id}` : null}
    />
  );
}

// Netflix-style numbered rail — giant numeral behind each tile.
function Top10Rail({ title, items, onPlay, editHref }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid="tv-top10-rail" className="mx-[var(--tv-safe-x)] relative">
      <div className="flex items-center gap-2 mb-2">
        <Flame className="w-5 h-5 text-tv-accent" />
        <h3 className="font-display font-black text-xl text-white">{title}</h3>
        {editHref && <AdminEditPencil href={editHref} />}
      </div>
      <div className="flex gap-6 overflow-x-auto pb-2 pl-8">
        {items.slice(0, 10).map((v, i) => (
          <button
            key={v.id || i}
            onClick={() => onPlay?.(v)}
            data-testid={`tv-top10-item-${i + 1}`}
            className="shrink-0 relative group text-left"
          >
            <span
              aria-hidden
              className="absolute -left-8 -bottom-3 font-display font-black leading-none text-white/10 select-none pointer-events-none"
              style={{ fontSize: "9rem", WebkitTextStroke: "2px rgba(255,255,255,0.5)" }}
            >{i + 1}</span>
            <div className="w-48 aspect-[2/3] bg-black rounded-lg overflow-hidden relative z-10 group-hover:ring-2 group-hover:ring-tv-accent transition-all">
              {v.thumbnail_url
                ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-gradient-to-br from-fuchsia-900 to-black" />}
            </div>
            <div className="mt-1.5 font-bold text-white text-sm truncate max-w-[12rem]">{v.title}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ==========================================================================
// HERO — Rotating carousel (5 titles, 9s each) with muted trailer preview
// ==========================================================================
function HeroCarousel({ items, onPlay }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const { user } = useAuth();
  const [watchlistIds, setWatchlistIds] = useState(new Set());
  const vref = useRef(null);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 9000);
    return () => clearInterval(t);
  }, [items.length, paused]);

  useEffect(() => {
    if (!user) return;
    api.get("/watchlist").then((r) => {
      setWatchlistIds(new Set((r.data || []).map((v) => v.id)));
    }).catch(() => {});
  }, [user]);

  const cur = items[idx];
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => { setImgOk(true); }, [cur?.id]);

  if (!cur) return null;
  const inList = watchlistIds.has(cur.id);
  const trailerSrc = cur.trailer_url || cur.preview_url || (cur.is_external ? null : cur.video_url);

  const toggleList = async () => {
    if (!user) return;
    const was = inList;
    // Optimistic update
    setWatchlistIds((s) => {
      const n = new Set(s);
      if (was) n.delete(cur.id); else n.add(cur.id);
      return n;
    });
    try {
      if (was) await api.delete(`/watchlist/${cur.id}`);
      else await api.post(`/watchlist/${cur.id}`);
    } catch {
      // Revert on failure
      setWatchlistIds((s) => {
        const n = new Set(s);
        if (was) n.add(cur.id); else n.delete(cur.id);
        return n;
      });
    }
  };

  // Deterministic gradient per hero item so repeat views feel designed
  const brandGrad = (() => {
    const palette = [
      "from-[#7a1020] via-[#3a0a14] to-[#0a0a0a]",
      "from-[#9b1230] via-[#3b0b1a] to-[#080204]",
      "from-[#b3591d] via-[#3a1a0d] to-[#0a0505]",
      "from-[#1f2a7a] via-[#0d1230] to-[#05060f]",
      "from-[#5a1070] via-[#22082c] to-[#0a0510]",
    ];
    const s = (cur.id || cur.title || "").toString();
    let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return palette[Math.abs(h) % palette.length];
  })();

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ height: "var(--tv-hero-h)" }}
      data-testid="tv-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Brand gradient base — always visible (fallback when artwork missing) */}
      <div className={`absolute inset-0 bg-gradient-to-br ${brandGrad}`} />
      {/* Background artwork */}
      {cur.thumbnail_url && imgOk && (
        <img
          key={`img-${cur.id}`}
          src={cur.thumbnail_url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ animation: "tvHeroFade 600ms ease" }}
          onError={() => setImgOk(false)}
        />
      )}
      {/* Tap IN watermark when no artwork */}
      {(!cur.thumbnail_url || !imgOk) && (
        <img
          src="/tapintv-logo-v2.png"
          alt=""
          aria-hidden
          className="absolute right-[6%] top-1/2 -translate-y-1/2 opacity-[0.18] pointer-events-none select-none"
          style={{ width: "42vmin", maxWidth: 720 }}
          draggable={false}
        />
      )}
      {trailerSrc && !cur.is_external && (
        <video
          ref={vref}
          key={`v-${cur.id}`}
          src={trailerSrc}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-0"
          onCanPlay={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transition = "opacity 800ms ease"; }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      {/* Left-to-right darkening for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 via-40% to-transparent" />
      {/* Vignette + bottom fade into first rail */}
      <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/85 to-transparent" />
      {/* Faint grain */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-[0.08]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
          backgroundSize: "320px 320px",
        }}
      />

      <div
        className="relative z-10 h-full flex flex-col justify-end"
        style={{ padding: "0 var(--tv-safe-x) calc(var(--tv-safe-y) * 2)", maxWidth: "min(920px, 58vw)" }}
      >
        <div className="text-white/70 uppercase tracking-[0.25em] font-bold text-[0.75em] mb-3">● Featured on Tap IN TV</div>
        <h1 className="font-display text-[3.4em] font-black leading-[1.02] mb-3 drop-shadow-2xl">{cur.title}</h1>
        <p className="text-white/85 text-[1.05em] mb-5 line-clamp-2 max-w-[640px] drop-shadow-xl">
          {cur.description || "New on Tap IN TV"}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            data-tv-focus="true"
            data-testid="tv-hero-play"
            onClick={() => onPlay(toPlayable(cur))}
            className="tv-focusable inline-flex items-center gap-3 bg-white hover:bg-white/90 text-black font-display font-black text-[1.1em] px-[1.6em] py-[0.75em] rounded-md"
          >
            <Play className="w-[1.15em] h-[1.15em] fill-current" /> Play
          </button>
          <button
            data-tv-focus="true"
            data-testid="tv-hero-mylist"
            onClick={toggleList}
            className="tv-focusable inline-flex items-center gap-3 bg-white/15 hover:bg-white/25 text-white font-display font-bold text-[1.05em] px-[1.4em] py-[0.75em] rounded-md border border-white/10 backdrop-blur"
          >
            {inList ? <Check className="w-[1.1em] h-[1.1em]" /> : <Plus className="w-[1.1em] h-[1.1em]" />}
            {inList ? "In My List" : "My List"}
          </button>
          <button
            data-tv-focus="true"
            data-testid="tv-hero-info"
            onClick={() => onPlay(toPlayable(cur))}
            className="tv-focusable inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 text-white font-bold text-[1em] px-[1.3em] py-[0.75em] rounded-md"
          >
            <Info className="w-[1.1em] h-[1.1em]" /> More Info
          </button>
        </div>
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-[1.8em] right-[var(--tv-safe-x)] flex items-center gap-2 z-10">
        {items.map((_, i) => (
          <button
            key={i}
            aria-label={`Slide ${i + 1}`}
            data-testid={`tv-hero-dot-${i}`}
            onClick={() => setIdx(i)}
            className="tv-hero-dot h-[6px] rounded-full"
            style={{
              width: i === idx ? 34 : 12,
              background: i === idx ? "#fff" : "rgba(255,255,255,0.4)",
            }}
          />
        ))}
      </div>
      <style>{`@keyframes tvHeroFade { from { opacity: 0.5; } to { opacity: 1; } }`}</style>
    </div>
  );
}

// ==========================================================================
// LIVE tab
// ==========================================================================
function LiveTab({ onPlay }) {
  const [live, setLive] = useState(null);
  useEffect(() => { api.get("/live").then((r) => setLive(r.data || [])).catch(() => setLive([])); }, []);
  if (live === null) return <TvLoading />;
  if (live.length === 0) return <TvEmpty title="Nothing live right now" subtitle="Check back soon — creators go live every day." />;
  return (
    <div style={{ padding: "calc(var(--tv-safe-y) * 2 + 6em) var(--tv-safe-x) var(--tv-safe-y)" }}>
      <h2 className="font-display text-[1.9em] font-black mb-[var(--tv-gap)]">On Air Now</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[var(--tv-gap)]">
        {live.map((s) => (
          <button
            key={s.id}
            data-tv-focus="true"
            data-testid={`tv-live-${s.id}`}
            onClick={() => onPlay(toPlayableLive(s))}
            className="tv-focusable text-left rounded-lg overflow-hidden bg-white/5 border border-white/10 transition"
          >
            <div className="aspect-video bg-black relative">
              {s.thumbnail_url && <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />}
              <div className="absolute top-2 left-2 bg-red-600 text-white text-[0.7em] font-bold px-[0.8em] py-[0.25em] rounded uppercase tracking-wider">● LIVE</div>
            </div>
            <div className="p-[0.9em]">
              <div className="font-display font-bold text-[1.05em] line-clamp-1">{s.title || "Untitled Stream"}</div>
              <div className="text-white/60 text-[0.85em] mt-1">{s.creator_name || "Creator"}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ==========================================================================
// CHANNELS tab
// ==========================================================================
function ChannelsTab({ onPlay }) {
  const [cats, setCats] = useState(null);
  const [cat, setCat] = useState("__all__"); // "__all__" sentinel = no filter
  const [videos, setVideos] = useState(null);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])).catch(() => setCats([])); }, []);
  useEffect(() => {
    // Build the request: empty/all → fetch the recent feed; otherwise filter.
    const url = cat && cat !== "__all__"
      ? `/videos?category=${encodeURIComponent(cat)}&limit=60`
      : `/videos?limit=60`;
    setVideos(null); // show loader between switches
    api.get(url).then((r) => {
      // Filter out image-only entries (e.g. news_promo stories with empty
      // video_url) — these don't play in the TV App and trigger the generic
      // "Playback failed" fallback. Keep entries that have *any* playable
      // url OR are explicitly external embeds.
      const list = (r.data || []).filter((v) => {
        if (v.source_kind === "news_promo") return false;
        if (v.is_external && (v.embed_url || v.video_url)) return true;
        return Boolean(v.video_url || v.hls_url || v.embed_url);
      });
      setVideos(list);
    }).catch(() => setVideos([]));
  }, [cat]);
  if (cats === null) return <TvLoading />;
  return (
    <div style={{ padding: "calc(var(--tv-safe-y) * 2 + 6em) var(--tv-safe-x) var(--tv-safe-y)" }} className="space-y-[var(--tv-gap)]">
      <h2 className="font-display text-[1.9em] font-black">Channels</h2>
      <div className="flex flex-wrap gap-2">
        <button
          data-tv-focus="true"
          data-testid="tv-channel-all"
          onClick={() => setCat("__all__")}
          className={`tv-focusable px-[1.2em] py-[0.5em] rounded-full font-bold text-[1em] transition ${
            cat === "__all__" ? "bg-white text-black" : "bg-white/8 text-white/85 hover:bg-white/15"
          }`}
        >
          All
        </button>
        {cats.map((c) => {
          const slug = c.slug || c.id || c.name;
          const active = slug === cat;
          return (
            <button
              key={slug}
              data-tv-focus="true"
              data-testid={`tv-channel-${slug}`}
              onClick={() => setCat(slug)}
              className={`tv-focusable px-[1.2em] py-[0.5em] rounded-full font-bold text-[1em] transition ${
                active ? "bg-white text-black" : "bg-white/8 text-white/85 hover:bg-white/15"
              }`}
            >
              {c.name || slug}
            </button>
          );
        })}
      </div>
      {videos === null ? (
        <TvLoading />
      ) : videos.length === 0 ? (
        <TvEmpty title="No titles in this channel yet" subtitle="Try a different channel above — new uploads land here as creators publish." />
      ) : (
        <div data-testid="tv-channels-grid" className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-[var(--tv-gap)]">
          {videos.map((v) => <TvCard key={v.id} video={v} onPlay={onPlay} />)}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// PPV tab — dedicated Pay-Per-View channel. Lists every PPV event with
// price, host, optional countdown to a scheduled event time. Tapping a tile
// opens TvPlayer in preview mode (free seconds → paywall).
// ==========================================================================
function PpvTab({ onPlay }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [accessMap, setAccessMap] = useState({}); // {video_id: bool}

  useEffect(() => {
    api.get("/tv/ppv").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  // Pre-fetch access for signed-in users so unlocked titles play directly.
  useEffect(() => {
    if (!user || !items || items.length === 0) return;
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(items.slice(0, 60).map(async (v) => {
        try {
          const r = await api.get(`/videos/${v.id}/ppv/access`);
          map[v.id] = !!r.data?.has_access;
        } catch { map[v.id] = false; }
      }));
      if (!cancelled) setAccessMap(map);
    })();
    return () => { cancelled = true; };
  }, [user, items]);

  if (items === null) return <TvLoading />;

  const upcoming = items.filter((v) => v.ppv_event_at && new Date(v.ppv_event_at).getTime() > Date.now());
  const onDemand = items.filter((v) => !upcoming.includes(v));

  if (items.length === 0) {
    return <TvEmpty title="No PPV events yet" subtitle="Creators and the network will list ticketed shows here. Check back soon." />;
  }

  return (
    <div style={{ padding: "calc(var(--tv-safe-y) * 2 + 6em) var(--tv-safe-x) var(--tv-safe-y)" }} className="space-y-[calc(var(--tv-gap)*2)]">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[0.75em] uppercase tracking-[0.3em] text-tv-accent font-bold inline-flex items-center gap-2">
            <Ticket className="w-4 h-4" /> Pay-Per-View
          </div>
          <h2 className="font-display text-[2em] font-black mt-1">Tap IN PPV</h2>
          <div className="text-white/55 text-[0.95em] max-w-[60em] mt-1">
            Buy a ticket once and stream the event in HD. Creators set the price; the network splits revenue with the host. Sample every show free before you buy.
          </div>
        </div>
      </header>

      {upcoming.length > 0 && (
        <section data-testid="tv-ppv-upcoming">
          <h3 className="font-display font-black text-[1.4em] mb-3 inline-flex items-center gap-2">
            <Clock className="w-5 h-5 text-tv-accent" /> Upcoming events
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[var(--tv-gap)]">
            {upcoming.map((v) => <PpvCard key={v.id} item={v} owned={!!accessMap[v.id]} onPlay={onPlay} />)}
          </div>
        </section>
      )}

      <section data-testid="tv-ppv-on-demand">
        <h3 className="font-display font-black text-[1.4em] mb-3">All PPV titles</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[var(--tv-gap)]">
          {onDemand.map((v) => <PpvCard key={v.id} item={v} owned={!!accessMap[v.id]} onPlay={onPlay} />)}
        </div>
      </section>
    </div>
  );
}

function PpvCard({ item, owned, onPlay }) {
  const evt = item.ppv_event_at ? new Date(item.ppv_event_at).getTime() : 0;
  const ms = evt - Date.now();
  const upcoming = evt > Date.now();
  const days = Math.max(0, Math.floor(ms / 86_400_000));
  const hours = Math.max(0, Math.floor((ms % 86_400_000) / 3_600_000));
  const mins = Math.max(0, Math.floor((ms % 3_600_000) / 60_000));
  const cd = upcoming
    ? days > 0 ? `Starts in ${days}d ${hours}h` : hours > 0 ? `Starts in ${hours}h ${mins}m` : `Starts in ${mins}m`
    : null;
  return (
    <button
      data-tv-focus="true"
      data-testid={`tv-ppv-card-${item.id}`}
      onClick={() => onPlay(toPlayablePpv(item, owned))}
      className="tv-focusable text-left rounded-xl overflow-hidden bg-white/5 border border-white/10 group transition"
    >
      <div className="aspect-video bg-black relative">
        {item.thumbnail_url
          ? <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-fuchsia-900 via-rose-900 to-black" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-tv-accent text-black text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded">
          <Ticket className="w-3 h-3" /> PPV
        </div>
        {owned && (
          <div className="absolute top-2 right-2 bg-emerald-500 text-black text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Unlocked
          </div>
        )}
        {cd && !owned && (
          <div className="absolute bottom-2 left-2 bg-black/85 text-white text-[10px] font-bold px-2 py-0.5 rounded">{cd}</div>
        )}
        <div className="absolute bottom-2 right-2 bg-black/85 text-white font-display font-black text-[1.05em] px-2 py-0.5 rounded">
          ${Number(item.ppv_price_usd || 0).toFixed(2)}
        </div>
      </div>
      <div className="p-3">
        <div className="font-display font-bold text-[1em] text-white line-clamp-1">{item.title}</div>
        <div className="text-white/55 text-[0.85em] line-clamp-1">{item.creator_name || item.category || "Tap IN"}</div>
        <div className="text-tv-accent text-[0.75em] uppercase tracking-wider font-bold mt-2 inline-flex items-center gap-1">
          {owned ? "Watch now" : `Sample ${item.ppv_preview_seconds || 30}s free`}
          <Play className="w-3 h-3 fill-current" />
        </div>
      </div>
    </button>
  );
}

function toPlayablePpv(v, owned) {
  return {
    id: v.id,
    title: v.title,
    src: v.video_url || v.hls_url || v.embed_url || null,
    embed_url: v.embed_url,
    is_external: v.is_external,
    duration_sec: v.duration_sec || 0,
    progress_sec: 0,
    kind: "video",
    is_ppv: true,
    ppv_price_usd: v.ppv_price_usd,
    ppv_preview_seconds: v.ppv_preview_seconds || 30,
    ppv_event_at: v.ppv_event_at,
    ppv_access: !!owned,
    creator_id: v.creator_id,
    creator_name: v.creator_name,
    thumbnail_url: v.thumbnail_url,
  };
}

// ==========================================================================
// SEARCH tab
// ==========================================================================
function SearchTab({ onPlay }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const tRef = useRef(null);
  useEffect(() => {
    clearTimeout(tRef.current);
    if (!q.trim()) { setHits([]); return; }
    tRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setHits((data?.videos || []).slice(0, 24));
      } catch { setHits([]); }
    }, 350);
    return () => clearTimeout(tRef.current);
  }, [q]);
  return (
    <div style={{ padding: "calc(var(--tv-safe-y) * 2 + 6em) var(--tv-safe-x) var(--tv-safe-y)" }} className="space-y-[var(--tv-gap)]">
      <h2 className="font-display text-[1.9em] font-black">Search</h2>
      <input
        data-tv-focus="true"
        data-testid="tv-search-input"
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search titles, creators, categories…"
        className="tv-focusable w-full max-w-[70%] bg-white/10 border-2 border-white/20 rounded-2xl px-[1.4em] py-[0.8em] text-[1.4em] text-white outline-none focus:border-white"
      />
      {hits.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-[var(--tv-gap)]">
          {hits.map((v) => <TvCard key={v.id} video={v} onPlay={onPlay} />)}
        </div>
      )}
      {q.trim() && hits.length === 0 && (
        <div className="text-white/50 text-[1.15em]">No results for "{q}".</div>
      )}
    </div>
  );
}

// ==========================================================================
// MORE tab — sub-nav with Profile, Downloads, About
// ==========================================================================
function MoreTab() {
  const [section, setSection] = useState("profile");
  const sections = [
    { key: "profile",   label: "Profile",   icon: User },
    { key: "wallet",    label: "Wallet",    icon: Ticket },
    { key: "downloads", label: "Download",  icon: Download },
    { key: "about",     label: "About",     icon: Info },
  ];
  return (
    <div style={{ padding: "calc(var(--tv-safe-y) * 2 + 6em) var(--tv-safe-x) var(--tv-safe-y)" }} className="space-y-[var(--tv-gap)]">
      <div className="flex gap-2 flex-wrap">
        {sections.map((s) => {
          const Icon = s.icon;
          const active = s.key === section;
          return (
            <button
              key={s.key}
              data-tv-focus="true"
              data-testid={`tv-more-${s.key}`}
              onClick={() => setSection(s.key)}
              className={`tv-focusable flex items-center gap-2 px-[1.3em] py-[0.6em] rounded-full font-bold text-[1em] transition ${
                active ? "bg-white text-black" : "bg-white/8 text-white/85 hover:bg-white/15"
              }`}
            >
              <Icon className="w-[1.1em] h-[1.1em]" /> {s.label}
            </button>
          );
        })}
      </div>
      {section === "profile" && <ProfileSection />}
      {section === "wallet" && <TvWalletSection />}
      {section === "downloads" && <DownloadsSection />}
      {section === "about" && <AboutSection />}
    </div>
  );
}

// TV-friendly wallet view: balance card + add-funds chips. The full activity
// ledger lives at /funds (route opens in same tab); TV viewers usually only
// need to top up before tipping or unlocking PPV.
function TvWalletSection() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(0);
  const [phonePay, setPhonePay] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/funds/me").then((r) => setData(r.data)).catch(() => setData({ balance_usd: 0, points_balance: 0, preset_amounts: [5, 10, 25, 50, 100] }));
  }, [user]);

  const refresh = () => api.get("/funds/me").then((r) => setData(r.data)).catch(() => {});

  // Poll while QR shown so balance reflects deposit when user pays on phone.
  useEffect(() => {
    if (!phonePay) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [phonePay]);

  const startDeposit = async (amount, mode = "phone") => {
    setBusy(amount);
    try {
      const { data: r } = await api.post("/funds/deposit/checkout", {
        amount: Number(amount),
        origin_url: window.location.origin,
      });
      // On TV, default is "phone" QR — Stripe redirects on Tizen/webOS are awful.
      if (mode === "stripe") {
        window.open(r.url, "_blank", "noopener,noreferrer");
      } else {
        setPhonePay({ url: r.qr_url || r.url, amount: r.amount_usd });
      }
    } catch (e) {
      // No toast on TV (no sonner mounted on tv-app); fall back to alert.
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't start deposit");
    } finally {
      setBusy(0);
    }
  };

  if (!user) {
    return (
      <div className="max-w-[60em]">
        <h2 className="font-display text-[1.9em] font-black">Wallet</h2>
        <p className="text-white/65 mt-3 text-[1em]">Sign in to see your balance, top up, and tip the host.</p>
      </div>
    );
  }
  if (!data) return <TvLoading />;

  const presets = data.preset_amounts || [5, 10, 25, 50, 100];

  return (
    <div data-testid="tv-wallet-section" className="max-w-[68em] space-y-[var(--tv-gap)]">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[0.75em] uppercase tracking-[0.3em] text-tv-accent font-bold inline-flex items-center gap-2">
            <Ticket className="w-4 h-4" /> Spending wallet
          </div>
          <h2 data-testid="tv-wallet-balance" className="font-display font-black text-[2.6em] mt-1">${Number(data.balance_usd || 0).toFixed(2)}</h2>
          <div className="text-white/55 text-[0.95em]">
            {data.points_balance > 0 && <>Points: <strong className="text-white">{data.points_balance}</strong> · </>}
            Top up to instantly tip creators or unlock PPV.
          </div>
        </div>
        <button
          data-tv-focus="true"
          data-testid="tv-wallet-refresh"
          onClick={refresh}
          className="tv-focusable inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold text-[0.9em] px-4 py-2 rounded-full"
        >Refresh</button>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <h3 className="font-display font-bold text-[1.2em]">Add funds</h3>
        <p className="text-white/55 text-[0.9em] mt-1">Tap a preset — we'll show a QR code so you can pay from your phone.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {presets.map((amt) => (
            <button
              key={amt}
              data-tv-focus="true"
              data-testid={`tv-wallet-deposit-${amt}`}
              disabled={busy === amt}
              onClick={() => startDeposit(amt, "phone")}
              className="tv-focusable inline-flex items-center gap-1 bg-tv-accent hover:bg-yellow-300 text-black font-display font-black text-[1em] px-5 py-2.5 rounded-full disabled:opacity-60"
            >
              {busy === amt ? "…" : "+"}${amt}
            </button>
          ))}
        </div>
        <div className="text-white/45 text-[0.75em] mt-3 inline-flex items-center gap-2">
          Stripe-secured · TVs scan a QR to pay on phone
        </div>
      </div>

      {phonePay && (
        <TvPhonePayQr url={phonePay.url} amount={phonePay.amount} onClose={() => { setPhonePay(null); refresh(); }} />
      )}
    </div>
  );
}

function TvPhonePayQr({ url, amount, onClose }) {
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(url)}`;
  return (
    <div data-testid="tv-wallet-qr" className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-6">
      <div className="bg-[#0a0a0a] border border-white/15 rounded-2xl p-8 max-w-lg w-full text-center">
        <div className="text-[0.8em] uppercase tracking-[0.3em] text-tv-accent font-bold inline-flex items-center gap-2 justify-center">
          <Ticket className="w-4 h-4" /> Pay on your phone
        </div>
        <h3 className="font-display font-black text-[1.8em] text-white mt-2">Scan to add ${Number(amount).toFixed(2)}</h3>
        <div className="bg-white p-4 rounded-xl inline-block mt-5">
          <img src={qrSrc} alt="QR code to Stripe Checkout" width={320} height={320} className="block" />
        </div>
        <p className="text-white/65 text-[1em] mt-4 max-w-md mx-auto">
          Open your phone's camera and point it at the code. After payment, your TV wallet will update automatically.
        </p>
        <button
          data-tv-focus="true"
          data-testid="tv-wallet-qr-close"
          onClick={onClose}
          className="tv-focusable mt-6 bg-tv-accent hover:bg-yellow-300 text-black font-display font-black px-6 py-3 rounded-full"
        >Done</button>
      </div>
    </div>
  );
}

function ProfileSection() {
  const { user, logout } = useAuth();
  if (!user) return <TvLink />;
  return (
    <div className="max-w-[80%] space-y-[var(--tv-gap)]">
      <h2 className="font-display text-[1.9em] font-black">Your Profile</h2>
      <div className="flex items-center gap-[var(--tv-gap)] bg-white/5 rounded-2xl p-[1.4em] border border-white/10">
        <div className="w-[3.6em] h-[3.6em] rounded-full bg-white text-black font-black text-[1.9em] flex items-center justify-center">
          {(user.name || user.email || "?")[0]?.toUpperCase()}
        </div>
        <div>
          <div className="text-[1.6em] font-display font-black">{user.name || user.email}</div>
          <div className="text-white/60 text-[1em]">{user.email}</div>
          <div className="text-white/70 uppercase tracking-wider text-[0.75em] mt-1">{user.role || "viewer"}</div>
        </div>
      </div>
      <button
        data-tv-focus="true"
        data-testid="tv-signout"
        onClick={logout}
        className="tv-focusable bg-white/10 hover:bg-white/20 text-white font-bold text-[1em] px-[1.6em] py-[0.75em] rounded-full"
      >
        Sign out of this TV
      </button>
    </div>
  );
}

// Download tab — step-by-step for every major platform
function DownloadsSection() {
  const platforms = [
    {
      key: "ios",
      name: "iPhone & iPad",
      icon: Smartphone,
      tag: "iOS 15+",
      color: "from-sky-500/30 to-blue-500/10 border-sky-400/30",
      url: "https://apps.apple.com/",
      steps: [
        "Open the App Store on your iPhone or iPad.",
        "Tap Search and type \"Tap IN TV\".",
        "Tap Get, then authenticate with Face ID / Touch ID.",
        "Open the app and sign in with your Tap IN TV account.",
        "Tap the \"Link a TV\" option in Settings to connect this device.",
      ],
    },
    {
      key: "android",
      name: "Android Phone & Tablet",
      icon: Smartphone,
      tag: "Android 9+",
      color: "from-emerald-500/30 to-green-500/10 border-emerald-400/30",
      url: "https://play.google.com/store",
      steps: [
        "Open the Google Play Store.",
        "Search for \"Tap IN TV\" in the search bar.",
        "Tap Install, wait for the download to complete.",
        "Open the app and sign in with your Tap IN TV account.",
        "Enable notifications to get creator go-live alerts.",
      ],
    },
    {
      key: "firetv",
      name: "Amazon Fire TV",
      icon: TvIcon,
      tag: "Fire OS 6+",
      color: "from-orange-500/30 to-amber-500/10 border-orange-400/30",
      url: "https://www.amazon.com/firetv",
      steps: [
        "From the Fire TV home screen, highlight the search icon.",
        "Type \"Tap IN TV\" using the on-screen keyboard or Alexa voice.",
        "Select the Tap IN TV app from the results.",
        "Choose Get or Download — it's free.",
        "Launch, and enter the pairing code from this TV in Settings → Link device.",
      ],
    },
    {
      key: "roku",
      name: "Roku",
      icon: TvIcon,
      tag: "Roku OS 10+",
      color: "from-purple-500/30 to-fuchsia-500/10 border-purple-400/30",
      url: "https://channelstore.roku.com/",
      steps: [
        "Press the Home button on your Roku remote.",
        "Scroll to Streaming Channels → Search Channels.",
        "Type \"Tap IN TV\" and select the channel.",
        "Select Add Channel. Wait ~30 seconds.",
        "Open the channel and enter your 6-digit pairing code.",
      ],
    },
    {
      key: "appletv",
      name: "Apple TV",
      icon: TvIcon,
      tag: "tvOS 15+",
      color: "from-white/20 to-white/5 border-white/20",
      url: "https://www.apple.com/apple-tv-app/",
      steps: [
        "From the Apple TV home screen, open the App Store.",
        "Go to Search and type \"Tap IN TV\".",
        "Select Get to download.",
        "Launch Tap IN TV and sign in with the on-screen code.",
        "Use the Siri Remote's touchpad to navigate rails.",
      ],
    },
    {
      key: "samsung",
      name: "Samsung Smart TV",
      icon: Monitor,
      tag: "Tizen 5.5+",
      color: "from-blue-400/30 to-indigo-500/10 border-blue-400/30",
      url: "https://www.samsung.com/us/tvs/all-tvs/?smart-tv=all-smart-tvs",
      steps: [
        "Press the Home button on your Samsung remote.",
        "Open the Apps section and tap the magnifying glass.",
        "Search \"Tap IN TV\" and press Enter.",
        "Select Install, then Open.",
        "Enter the pairing code shown on the TV's Link screen.",
      ],
    },
    {
      key: "lg",
      name: "LG Smart TV",
      icon: Monitor,
      tag: "webOS 5.0+",
      color: "from-pink-500/30 to-rose-500/10 border-pink-400/30",
      url: "https://www.lg.com/us/tvs",
      steps: [
        "Press the Home button on your LG Magic Remote.",
        "Open the LG Content Store.",
        "Search \"Tap IN TV\" using the pointer or voice.",
        "Select Install — app will auto-launch after install.",
        "Enter the pairing code from your account to sync.",
      ],
    },
    {
      key: "androidtv",
      name: "Android TV / Google TV",
      icon: TvIcon,
      tag: "Android TV 10+",
      color: "from-emerald-400/30 to-teal-500/10 border-emerald-400/30",
      url: "https://play.google.com/store",
      steps: [
        "Go to the Apps row on your Android TV / Google TV.",
        "Open the Play Store.",
        "Search \"Tap IN TV\" and select the app.",
        "Choose Install.",
        "Open Tap IN TV and enter the 6-digit TV pairing code.",
      ],
    },
  ];
  return (
    <div className="space-y-[var(--tv-gap)]">
      <div>
        <h2 className="font-display text-[1.9em] font-black">Get Tap IN TV on all your devices</h2>
        <p className="text-white/60 text-[1em] mt-1 max-w-[60em]">
          Tap IN TV is currently rolling out everywhere. Follow the step-by-step instructions for your device below.
          Once installed, link it to this account using the pairing code from the Profile tab.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--tv-gap)]">
        {platforms.map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.key}
              data-testid={`tv-download-${p.key}`}
              className={`rounded-2xl p-[1.3em] bg-gradient-to-br ${p.color} border backdrop-blur`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-[2.6em] h-[2.6em] rounded-xl bg-black/40 border border-white/15 flex items-center justify-center">
                    <Icon className="w-[1.3em] h-[1.3em] text-white" />
                  </div>
                  <div>
                    <div className="font-display font-black text-[1.15em]">{p.name}</div>
                    <div className="text-white/60 text-[0.8em] uppercase tracking-wider">{p.tag}</div>
                  </div>
                </div>
                <a
                  data-tv-focus="true"
                  data-testid={`tv-download-link-${p.key}`}
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="tv-focusable inline-flex items-center gap-2 bg-white text-black font-bold text-[0.85em] px-[1em] py-[0.5em] rounded-full hover:bg-white/85"
                >
                  Open Store <ExternalLink className="w-[0.95em] h-[0.95em]" />
                </a>
              </div>
              <ol className="space-y-2 text-white/85 text-[0.95em] list-none">
                {p.steps.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="shrink-0 w-[1.7em] h-[1.7em] rounded-full bg-white/10 border border-white/15 text-white/85 text-[0.85em] font-black flex items-center justify-center mt-[0.1em]">{i + 1}</span>
                    <span className="leading-snug">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          );
        })}
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-[1.3em]">
        <div className="font-display font-black text-[1.1em] mb-2">Need help pairing?</div>
        <p className="text-white/70 text-[0.95em] max-w-[60em]">
          Once any app is installed, open Settings on that device and look for <em>Link a TV</em>.
          Enter the 6-digit code from the Profile tab of this TV, and both devices will sync
          your watchlist, continue-watching progress, and sign-in instantly.
        </p>
      </div>
    </div>
  );
}

function AboutSection() {
  return (
    <div className="max-w-[60em] space-y-[var(--tv-gap)]">
      <h2 className="font-display text-[1.9em] font-black">About Tap IN TV</h2>
      <p className="text-white/80 text-[1.05em] leading-relaxed">
        Tap IN TV is a streaming network for independent creators — film, music, live shows,
        cooking, kids, gospel, and more. Stream it. Create it. Go live. Own your channel.
      </p>
      <div className="grid grid-cols-2 gap-[var(--tv-gap)] text-[0.95em]">
        <InfoRow label="Version" value="Smart TV 1.2.0" />
        <InfoRow label="Device" value="Web TV" />
        <InfoRow label="Support" value="help@tapintv.com" />
        <InfoRow label="Legal" value="Tap IN TV Network, Inc." />
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-[1em]">
      <div className="text-white/50 uppercase tracking-wider text-[0.75em]">{label}</div>
      <div className="font-display font-bold text-[1.05em] mt-1">{value}</div>
    </div>
  );
}

// ==========================================================================
// Rail with left/right arrows + smaller Netflix-style cards
// ==========================================================================
function TvRail({ title, items, onPlay, showProgress, editHref }) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 10);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, items]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <section className="relative">
      <h3
        className="font-display text-[1.4em] font-black mb-[calc(var(--tv-rail-gap)*1.6)] relative inline-flex items-center gap-2"
        style={{ padding: "0 var(--tv-safe-x)" }}
      >
        {title}
        {editHref && (
          <a
            href={editHref}
            target="_blank" rel="noreferrer"
            data-testid="tv-rail-edit"
            className="text-xs bg-black/70 hover:bg-tv-accent hover:text-black text-white rounded-full p-1.5"
          ><Pencil className="w-3.5 h-3.5" /></a>
        )}
      </h3>
      <div className="tv-rail-wrap">
        {canLeft && (
          <button
            data-tv-focus="true"
            aria-label="Scroll left"
            onClick={() => scrollBy(-1)}
            className="tv-rail-arrow tv-focusable absolute left-1 top-0 bottom-0 z-10 w-[var(--tv-safe-x)] flex items-center justify-center bg-gradient-to-r from-black/75 to-transparent hover:from-black/90 text-white"
          >
            <ChevronLeft className="w-[2em] h-[2em]" />
          </button>
        )}
        <div
          ref={scrollRef}
          className="tv-rail flex gap-[var(--tv-rail-gap)] overflow-x-auto pb-[1em]"
        >
          {items.map((v) => (
            <TvCard
              key={v.id}
              video={v}
              onPlay={onPlay}
              progress={showProgress ? pctFor(v) : null}
            />
          ))}
        </div>
        {canRight && (
          <button
            data-tv-focus="true"
            aria-label="Scroll right"
            onClick={() => scrollBy(1)}
            className="tv-rail-arrow tv-focusable absolute right-1 top-0 bottom-0 z-10 w-[var(--tv-safe-x)] flex items-center justify-center bg-gradient-to-l from-black/75 to-transparent hover:from-black/90 text-white"
          >
            <ChevronRight className="w-[2em] h-[2em]" />
          </button>
        )}
      </div>
    </section>
  );
}

function pctFor(v) {
  const p = v.progress_sec || 0;
  const d = v.duration_sec || v.duration || 0;
  if (!p || !d) return null;
  return Math.min(100, Math.round((p / d) * 100));
}

function TvCard({ video, onPlay, progress }) {
  const [hot, setHot] = useState(false);
  const timerRef = useRef(null);
  const vref = useRef(null);

  // Netflix-style delayed hover-preview: wait 700ms before starting trailer
  // so quick scroll-by hovers don't thrash the video element.
  const onEnter = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setHot(true), 700);
  };
  const onLeave = () => {
    clearTimeout(timerRef.current);
    setHot(false);
  };
  const onFocus = () => { timerRef.current = setTimeout(() => setHot(true), 500); };
  const onBlur  = () => { clearTimeout(timerRef.current); setHot(false); };

  useEffect(() => () => clearTimeout(timerRef.current), []);
  useEffect(() => {
    if (hot && vref.current) { vref.current.currentTime = 0; vref.current.play?.().catch(() => {}); }
  }, [hot]);

  const trailer = video.trailer_url || video.preview_url
    || (!video.is_external && (video.hls_url || video.video_url));

  return (
    <button
      data-tv-focus="true"
      data-testid={`tv-card-${video.id}`}
      onClick={() => onPlay(toPlayable(video))}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      className="tv-card tv-focusable tv-card-scale shrink-0 text-left relative"
      style={{ width: "var(--tv-card-w)" }}
    >
      <div className="aspect-video rounded-md overflow-hidden bg-white/5 border border-white/10 relative">
        {video.thumbnail_url ? (
          <img src={video.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-white/5 to-white/0" />
        )}
        {hot && trailer && (
          <video
            ref={vref}
            src={trailer}
            muted
            loop
            playsInline
            autoPlay
            className="absolute inset-0 w-full h-full object-cover opacity-0"
            onCanPlay={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transition = "opacity 400ms ease"; }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        {typeof progress === "number" && progress > 0 && (
          <div className="absolute bottom-0 inset-x-0 h-[4px] bg-white/20">
            <div className="h-full bg-red-600" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
      <div className="tv-card-title mt-[0.5em]">
        <div className="font-display font-bold text-[0.95em] line-clamp-1">{video.title}</div>
        <div className="text-white/55 text-[0.8em] line-clamp-1">{video.creator_name || video.category}</div>
      </div>
    </button>
  );
}

function TvLoading() {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="animate-spin w-[3em] h-[3em] border-[3px] border-white border-t-transparent rounded-full" />
    </div>
  );
}

function TvEmpty({ title, subtitle }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center" style={{ padding: "calc(var(--tv-safe-y) * 2 + 6em) var(--tv-safe-x) 0" }}>
      <div className="font-display text-[2.4em] font-black mb-3">{title}</div>
      <div className="text-white/60 text-[1.15em] max-w-[60%]">{subtitle}</div>
    </div>
  );
}

function toPlayable(v) {
  return {
    id: v.id,
    title: v.title,
    src: v.video_url || v.hls_url || v.embed_url || null,
    embed_url: v.embed_url,
    is_external: v.is_external,
    duration_sec: v.duration_sec || v.duration || 0,
    progress_sec: v.progress_sec || 0,
    kind: v.is_short ? "short" : "video",
    is_ppv: !!v.is_ppv,
    ppv_price_usd: v.ppv_price_usd || null,
    ppv_preview_seconds: v.ppv_preview_seconds || 30,
    thumbnail_url: v.thumbnail_url,
    creator_id: v.creator_id,
    creator_name: v.creator_name,
  };
}

// Build the player payload for a LIVE stream tile. Cloudflare-backed
// streams expose `hls_playback_url`; legacy / simulated rows may carry
// `playback_url`, `hls_url`, or `video_url`. We pick whichever the
// backend gave us. If none are set, the player falls into its
// "stream not available" state instead of "Playback failed" so the
// user sees an actionable message.
function toPlayableLive(s) {
  const hls = s.hls_playback_url || s.playback_url || s.hls_url || s.video_url || s.embed_url || null;
  return {
    id: s.id,
    title: s.title || "Live stream",
    src: hls,
    embed_url: s.embed_url,
    is_external: !!s.is_external && !!s.embed_url,
    kind: "live",
    provider: s.provider || "unknown",
    creator_name: s.creator_name,
  };
}
